import { cloneDeep } from "lodash"
import { COLORS, findObjectByPathValue, roundNumber } from "../utils"
import { updateAllRecipesFieldsOnChange, updatePriorStepsInUlteriorComponents } from "./recipes"
import { convertKilosIntoGrams } from "./ressources"

/**
 * Recursively collects all components with a `supplierItem` property from the provided step components and their nested prior steps.
 *
 * @param {Array} stepComponents - An array of step component objects to search through.
 */
export const getAllSupplierItemsFromStepComponentsForSectionOutput = (stepComponents = []) => {
  const supplierItems = []

  const traverse = (components, priorStepsIndex) => {
    for (const component of components) {
      if (component.supplierItem) {
        const values = {
          netWeight: component.netWeight,
          supplierItem: {
            ...component.supplierItem,
            objectId: component.supplierItem.id || component.supplierItem.objectId,
            name: component.supplierItem.name
          },
          stepComponentIndex: component.index,
        }
        if (priorStepsIndex) {
          values.priorStepsIndex = priorStepsIndex
        }
        supplierItems.push(values)
      }
      if (component.priorSteps && component.priorSteps.stepComponents) {
        traverse(component.priorSteps.stepComponents, component.priorSteps.index)
      }
    }
  }

  traverse(stepComponents)

  return supplierItems
}

/**
 * Gets the last production step from a section.
 * It may return the last step directly or the last step from reusable steps if the last step is reusable.
 * @param section 
 * @returns 
 */
export const getSectionLastStep = (section) => {
  const productionSteps = section.productionSteps || []

  if (productionSteps.length === 0) return null

  const lastStep = productionSteps[productionSteps.length - 1]

  // if reusable, get the last step from the reusable steps
  if (lastStep?.reusable) {
    const reusableSteps = lastStep.step?.productionSteps || []
    if (reusableSteps.length === 0) return null
    const lastReusableStep = reusableSteps[reusableSteps.length - 1]
    return lastReusableStep
  }

  return lastStep?.step || null
}

/**
 * Retrieves all ingredients from the last production step of a given section.
 *
 * This function accesses the `productionSteps` array within the provided `section` object,
 * extracts the last step, and collects all supplier items (ingredients) from its step components.
 * If there are no production steps or the last step is undefined, it returns an empty array.
 *
 * @param lastStep - The last production step object from which to collect ingredients.
 * @returns An array of ingredient objects collected from the last production step's components.
 */
export const getAllIngredientsFromSection = (lastStep) => {
  const collectedIngredients = getAllSupplierItemsFromStepComponentsForSectionOutput(lastStep?.stepComponents || [])
  return collectedIngredients
}

/**
 * Generates the initial form values for section outputs and their ingredients.
 *
 * it initializes the form with a single output containing all ingredients from the section (if available).
 * Otherwise, it returns the existing `sectionOutputs` from the section.
 *
 * @param section - The section object containing outputs and ingredients, or null.
 */
export const getSectionOutputsIngredientsFormInitialValues = (section) => {
  if (!section) {
    return { sectionOutputs: [] }
  }
  const lastStep = getSectionLastStep(section)
  const supplierItems = section ? getAllIngredientsFromSection(lastStep) : []

  if (!section.sectionOutputs || section.sectionOutputs.filter(s => s.type === "ingredient").length === 0) {
    const type = "ingredient"

    return {
      // always 2 outputs by default, one for ingredients and one for empty output
      sectionOutputs: [
        {
          name: "",
          ingredients: supplierItems,
          type
        },
        {
          name: "",
          ingredients: [],
          type
        }
      ]
    }
  }
  const usedIngredients = section.sectionOutputs.reduce((acc, sO) => [...sO.ingredients, ...acc], [])
  const unusedIngredients = supplierItems.filter(sI => !usedIngredients.find(uSi => sI.stepComponentIndex === uSi.stepComponentIndex))
  return {
    sectionOutputs: section.sectionOutputs.map((sO, index) => {
      const ingredients = [
        ...sO.ingredients,
        ...(index === 0 ? unusedIngredients : [])
      ].map(sI => supplierItems.find(si => si.stepComponentIndex === sI.stepComponentIndex)).filter(Boolean)

      return {
        ...sO,
        ingredients
      }
    })
  }
}

/**
 * Display the section name, adding "(masquée)" if the section is set to be hidden in packaging,
 * or "(Totalement répartie)" if the section has content but all outputs are distributed elsewhere.
 * It's used in the recipe production steps table.
 * @param {*} section 
 * @returns 
 */
export const displaySectionName = (section) => {
  if (!section.name) return "-"

  let name = section.name
  
  // Check if section is fully distributed (has content but netWeight is 0)
  const isFullyDistributed = section.grossWeight > 0 && (!section.netWeight || section.netWeight === 0)
  
  if (isFullyDistributed) {
    name = name + " (totalement répartie)"
  } else if (isSectionHiddenInPackaging(section)) {
    name = name + " (masquée)"
  }

  return name
}

export const getSectionOutputsData = (section) => {
  if (!section || !section.sectionOutputs) {
    return []
  }
  const lastStep = getSectionLastStep(section)
  const supplierItems = lastStep ? getAllIngredientsFromSection(lastStep) : []
  const sectionOutputs = cloneDeep(section.sectionOutputs || [])
  return sectionOutputs.map((output, index) => {
    const name = output?.name || `${section.name} - Partie ${index + 1}`
    const ingredients = supplierItems
      .filter(ingredient => output?.percentage || output.ingredients.some(i =>
        i.stepComponentIndex === ingredient.stepComponentIndex
      ))
      .map(ingredient => ({ ...ingredient, netWeight: output?.percentage ? ingredient.netWeight * (output.percentage / 100) : ingredient.netWeight }))
    const quantity = output?.percentage ? `${output.percentage}%` : `${roundNumber(convertKilosIntoGrams(ingredients.reduce((acc, ingredient) => acc + (ingredient.netWeight || 0), 0), 2))} g`
    return {
      index: output.index,
      type: output.type,
      name: `${name} - ${quantity}`,
      sectionName: section.name,
      sectionId: section.id,
      percentage: output.percentage || null,
      realName: name,
      value: ingredients.map(ingredient => ingredient.supplierItem.name + " " + roundNumber(convertKilosIntoGrams(ingredient.netWeight), 2) + "g").join("; "),
      ingredients: ingredients.map(ingredient => ({
        ...ingredient,
        stepComponentIndex: ((output.type === "percentage" && index > 0) ? index + "_" : "") + ingredient.stepComponentIndex,
      })),
    }
  })
}

export const updateRecipeOnChangeSectionOutput = (recipe, section) => {

  if (recipe && section && section.sectionOutputs) {
    const sectionData = getSectionOutputsData(section)
    for (const sectionOutput of sectionData) {
      const truncatedSections = recipe.sections.slice(recipe.sections.findIndex(s => s.id === section.id) + 1)
      truncatedSections.forEach(sct => {
        const responses = findObjectByPathValue(sct, "productionSteps.step.stepComponents.sectionOutput.index", sectionOutput.index, { tree: true, recursive: true, path: true })
        responses.forEach(response => {
          const { path, parent } = response
          const matches = path.match(/\[(\d+)\]/g) || []
          // eslint-disable-next-line no-unused-vars
          const [_, productionStepIndex] = matches.map(m => parseInt(m.replace(/\[|\]/g, ""), 10))
          const currentStepComponent = response.stepComponents
          const previousSectionOutput = cloneDeep(response.sectionOutput)
          currentStepComponent.sectionOutput = {
            ...currentStepComponent.sectionOutput,
            ingredients: sectionOutput.ingredients,
            name: sectionOutput.name,
            value: sectionOutput.value,
            percentage: sectionOutput.percentage,
            netWeight: sectionOutput.ingredients.reduce((sum, ingredient, index) => sum + (ingredient.netWeight || 0) * (currentStepComponent?.sectionOutput?.stepComponents[index]?.transformRate || 100) / 100, 0),
            grossWeight: sectionOutput.ingredients.reduce((sum, ingredient) => sum + (ingredient.netWeight || 0), 0),
            stepComponents: sectionOutput.ingredients.map((ingredient) => {
              const realIndex = currentStepComponent.sectionOutput.stepComponents.findIndex(s => s.stepComponentIndex === ingredient.stepComponentIndex)
              const isSupplierItemChanged = realIndex > -1 && currentStepComponent.sectionOutput.stepComponents[realIndex]?.supplierItem?.objectId !== ingredient.supplierItem?.objectId
              const sCdata = {
                ...ingredient,
                transformationMode: null,
                transformRate: 100,
                transformationRate: 100, //@TODO transformationRate or transformRate
                ...(realIndex > -1 && !isSupplierItemChanged) && currentStepComponent.sectionOutput.stepComponents[realIndex] ? currentStepComponent.sectionOutput.stepComponents[realIndex] : {},
              }
              return {
                ...sCdata,
                netWeight: (ingredient.netWeight || 0) * (currentStepComponent?.sectionOutput?.stepComponents[realIndex]?.transformRate || 100) / 100,
                grossWeight: ingredient.netWeight || 0
              }
            }, [])
          }

          const deletedComponents = previousSectionOutput.stepComponents.filter(prevComp => !currentStepComponent.sectionOutput.stepComponents.find(currComp => currComp.stepComponentIndex === prevComp.stepComponentIndex))
          const addedComponents = currentStepComponent.sectionOutput.stepComponents.filter(currComp => !previousSectionOutput.stepComponents.find(prevComp => prevComp.stepComponentIndex === currComp.stepComponentIndex))
          const changedComponents = currentStepComponent.sectionOutput.stepComponents.filter(currComp => {
            const prevComp = previousSectionOutput.stepComponents.find(prev => prev.stepComponentIndex === currComp.stepComponentIndex)
            return prevComp && prevComp.supplierItem?.objectId !== currComp.supplierItem?.objectId
          })
          const alteredComponents = [
            ...deletedComponents.map(dc => ({ stepComponent: dc, type: "DELETE" })),
            ...addedComponents.map(ac => ({ stepComponent: ac, type: "UPSERT" })),
            ...changedComponents.map(cc => ({ stepComponent: cc, type: "UPDATE" }))
          ]
          alteredComponents.forEach(({ stepComponent, type: method }) => {
            updatePriorStepsInUlteriorComponents({
              currentStepComponent: stepComponent,
              currentStepName: currentStepComponent.sectionOutput.name,
              currentStepIndexCode: currentStepComponent.sectionOutput.index,
              currentStepIndex: productionStepIndex,
              allSteps: parent.productionSteps,
              section: sct,
              recipe,
              method,
              isProductionSteps: true
            })
          })

          currentStepComponent.sectionOutput.stepComponents.forEach((sC) => {
            updateAllRecipesFieldsOnChange({
              recipe,
              section: sct,
              steps: parent.productionSteps,
              parentStep: response.productionSteps.step,
              currentStepComponentChanged: sC,
              isProductionSteps: true
            })
          })
        })
      })
    }
  }
}

const isSectionHiddenInPackaging = (section) => section.packagingDisplay === false

const isFullyDistributed = (section) => section.grossWeight > 0 && (!section.netWeight || section.netWeight === 0)

export const getSectionNameStyles = (section) => ({ 
  color: (isSectionHiddenInPackaging(section) || isFullyDistributed(section)) ? COLORS.BLACK_500 + " !important" : "inherit" 
})
